import React, { Suspense, useState } from 'react';
import {
  Card,
  Divider,
  Typography,
  Breadcrumb,
  Skeleton,
  Alert,
  notification,
  Button,
  Checkbox,
  Collapse,
  Row,
  Col,
  Spin,
} from 'antd';
import { CheckCircleOutlined, FileOutlined, DownOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom-v5-compat';
import { useQuery, useMutation } from '@tanstack/react-query';
import Title from 'antd/lib/typography/Title';
import { ReadmeIcon } from '~/components/assets/ReadmeIcon';

// Lazy load PdfViewer to avoid react-pdf breaking useQuery in code-split chunks
// See: TypeError "Cannot convert undefined or null to object" in React Query's defaultQueryOptions
const PdfViewer = React.lazy(() => import('~/components/PdfViewer/PdfViewer'));

import { useSession } from '~/hooks/useSession';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { partnerManagementService } from '~/services';
import { useLifecycle } from '~/hooks/useLifecycle';
import { Flex } from '~/components/Flex';
import { FEDEX_EULA_ACCEPT_LABEL } from '~/config/carrierEulaConfig';
import { FormButtons } from '~/components/form/FormButtons';
import { useMutationInvalidator } from '~/hooks/useMutationInvalidator';
import { useServiceMethods } from '~/hooks/queries/useCarriersFiltered';
import { formatPackagingName, PACKAGING_MATERIALS } from '~/utils/packagingUtils';
import { ShipiumTag } from '~/components/shared/ShipiumTag';
import { useScrollToAccept } from '~/hooks/useScrollToAccept';

const { Text } = Typography;

const FEDEX_EULA_TYPE = 'FEDEX_OFFLINE_LABELS_EULA' as const;

const FedexOfflineLabelsAgreement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sessionPartnerUrl, sessionPartnerId } = useSession();
  // Checkbox always starts unchecked on render (even if previously accepted)
  const [eulaCheckbox, setEulaCheckbox] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const returnUrl = searchParams.get('returnUrl');

  const partnerQuery = useQuery({
    ...useQueryCreator(partnerManagementService.partners.get, {
      args: [sessionPartnerId],
    }),
    enabled: !!sessionPartnerId,
  });

  // Query for current legal agreement state
  const legalAgreementQuery = useQuery({
    ...useQueryCreator(partnerManagementService.legalAgreements.get, {
      args: [sessionPartnerId, FEDEX_EULA_TYPE],
    }),
    enabled: !!sessionPartnerId,
  });

  // Derive acceptance status from query
  const fedexOfflineLabelsEulaAccepted = legalAgreementQuery.data?.accepted ?? false;

  // Mutation for changing state
  const changeStateMutation = useMutation<
    unknown,
    Error,
    { partnerId: string; payload: { legalAgreementType: typeof FEDEX_EULA_TYPE; accepted: boolean } }
  >({
    mutationFn: useMutationInvalidator(partnerManagementService.legalAgreements.changeState),
    onSuccess: () => {
      // Reset checkbox after submission
      setEulaCheckbox(false);

      notification.success({
        message: 'FedEx Offline Labels EULA Accepted',
        description: 'You have successfully accepted the FedEx Offline Labels EULA',
      });

      // Navigate back to the return URL if they came from another page
      if (returnUrl) {
        navigate(returnUrl);
      }
    },
    onError: (err) => {
      setError('Failed to save offline labels preference. Please try again.');
      console.error('Failed to save offline labels preference:', err);
    },
  });

  const serviceMethodsQuery = useServiceMethods({
    select: (serviceMethods) =>
      serviceMethods
        .filter((serviceMethod) => serviceMethod.carrierId === 'fedex')
        .filter((serviceMethod) => !serviceMethod.name.toLowerCase().includes('international'))
        .filter((serviceMethod) => !serviceMethod.name.toLowerCase().includes('nacional'))
        .map((serviceMethod) => serviceMethod.name)
        .sort((a, b) => a.localeCompare(b)),
  });

  // Scroll-to-accept detection (skip if already accepted)
  const { hasScrolledToBottom, bottomMarkerRef } = useScrollToAccept({
    alreadyAccepted: fedexOfflineLabelsEulaAccepted,
  });

  useLifecycle({
    onMount: () => {
      document.title = 'FedEx Offline Labels Agreement';
    },
  });

  const handleSubmit = () => {
    changeStateMutation.mutate({
      partnerId: sessionPartnerId,
      payload: {
        legalAgreementType: FEDEX_EULA_TYPE,
        accepted: true,
      },
    });
  };

  const handleCancel = () => {
    if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate(`/${sessionPartnerUrl}`);
    }
  };

  return (
    <Card>
      <Flex direction="vertical">
        <Flex direction="vertical" gap="none">
          <Flex direction="horizontal" justifyContent="space-between">
            <Breadcrumb separator=">">
              <Breadcrumb.Item>
                <a href={`/${sessionPartnerUrl}`}>
                  <Text>Home</Text>
                </a>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Text strong>FedEx Offline Labels Agreement</Text>
              </Breadcrumb.Item>
            </Breadcrumb>
            <a rel="noreferrer" target="_blank" href={`https://docs.shipium.com/docs/offline-labels`}>
              <ReadmeIcon style={{ fontSize: '20px' }} />
            </a>
          </Flex>
          <Divider />
        </Flex>

        <Flex direction="vertical" alignItems="center">
          <Skeleton loading={partnerQuery.isLoading || serviceMethodsQuery.isLoading || legalAgreementQuery.isLoading}>
            <Flex direction="vertical" gap="md" maxWidth={800}>
              <Flex direction="horizontal" justifyContent="space-between" alignItems="center">
                <Flex direction="horizontal" gap="md" alignItems="center">
                  <img src="/fedex_logo.png" alt="FedEx Logo" width={40} height={40} />
                  <Title style={{ margin: 0 }} level={3}>
                    FedEx Offline Labels End-User License Agreement
                  </Title>
                </Flex>
                {fedexOfflineLabelsEulaAccepted && (
                  <div>
                    <ShipiumTag>
                      <CheckCircleOutlined /> Accepted
                    </ShipiumTag>
                  </div>
                )}
              </Flex>

              <Alert
                type="info"
                showIcon
                message="This agreement is required to use FedEx Ship Manager® Server for offline labels."
              />
              {!fedexOfflineLabelsEulaAccepted ? (
                <>
                  <Text>
                    By clicking the "I accept" button, you accept and agree to be bound by all of the provisions of this
                    Agreement, including those incorporated by reference, without modification, limitation or
                    qualification. You will be binding yourself or your company to this Agreement.
                  </Text>
                  <Text>
                    You represent that you have the authority to bind your company, if done on behalf of a company, to
                    this Agreement.{' '}
                    <Text strong>
                      If you do not have such authority, or if you do not agree with this Agreement, do not click "I
                      accept."
                    </Text>
                  </Text>
                  <Text>
                    If you do not accept, you will not be authorized to access or use the Application and/or the FedEx
                    services available through such Application.
                  </Text>
                </>
              ) : (
                <Text>You have already accepted the FedEx Offline Labels EULA.</Text>
              )}

              {/* PDF Scrollable Section - Only show if NOT accepted */}
              {!fedexOfflineLabelsEulaAccepted && (
                <Card
                  style={{
                    backgroundColor: '#fafafa',
                    border: '1px solid #d9d9d9',
                  }}
                >
                  <Flex direction="vertical" gap="sm">
                    <Flex direction="horizontal" justifyContent="space-between" alignItems="center">
                      <Text strong>Full License Agreement</Text>
                      {!hasScrolledToBottom && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <DownOutlined /> Scroll to bottom to enable acceptance
                        </Text>
                      )}
                    </Flex>
                    <div
                      style={{
                        maxHeight: '500px',
                        overflow: 'auto',
                        overflowX: 'hidden',
                        backgroundColor: '#fff',
                        border: '1px solid #e8e8e8',
                        padding: '16px',
                      }}
                    >
                      <Suspense
                        fallback={
                          <Flex direction="vertical" alignItems="center" justifyContent="center">
                            <Spin size="large" />
                            <Text type="secondary" style={{ marginTop: '16px' }}>
                              Loading PDF viewer...
                            </Text>
                          </Flex>
                        }
                      >
                        <PdfViewer
                          file="/fedex_standard_end_user_license_agreement_eula_for_distributed_automation_products.pdf"
                          bottomMarkerRef={bottomMarkerRef}
                        />
                      </Suspense>
                    </div>
                  </Flex>
                </Card>
              )}

              <Flex direction="horizontal" gap="md" justifyContent="flex-end" alignItems="center">
                <Button
                  type="link"
                  href="/fedex_standard_end_user_license_agreement_eula_for_distributed_automation_products.pdf"
                  target="_blank"
                >
                  <FileOutlined /> Open in New Tab
                </Button>
                {!fedexOfflineLabelsEulaAccepted && (
                  <Checkbox
                    disabled={!hasScrolledToBottom}
                    checked={eulaCheckbox}
                    onChange={(e) => setEulaCheckbox(e.target.checked)}
                  >
                    {FEDEX_EULA_ACCEPT_LABEL}
                  </Checkbox>
                )}
              </Flex>

              <Collapse>
                <Collapse.Panel header="Supported FedEx Service Methods" key="service_methods">
                  <Row gutter={[32, 16]}>
                    {serviceMethodsQuery.data?.map((serviceMethod) => (
                      <Col sm={24} md={24} lg={24} xl={12} key={serviceMethod}>
                        <Text strong>{serviceMethod}</Text>
                      </Col>
                    ))}
                  </Row>
                </Collapse.Panel>
                <Collapse.Panel header="Supported Special Services" key="special_services">
                  <Row gutter={[32, 16]}>
                    <Col sm={24} md={24} lg={24} xl={12}>
                      <Text strong>FedEx One Rate®</Text>
                    </Col>
                    <Col sm={24} md={24} lg={24} xl={12}>
                      <Flex direction="vertical" gap="none">
                        <Text strong>FedEx® Delivery Signature Options:</Text>
                        <ul>
                          <li>No Signature Required</li>
                          <li>Indirect Signature Required</li>
                          <li>Direct Signature Required</li>
                          <li>Adult Signature Required</li>
                        </ul>
                      </Flex>
                    </Col>
                    <Col sm={24} md={24} lg={24} xl={12}>
                      <Text strong>Residential Delivery</Text>
                    </Col>
                    <Col sm={24} md={24} lg={24} xl={12}>
                      <Text strong>Saturday Delivery</Text>
                    </Col>
                  </Row>
                </Collapse.Panel>
                <Collapse.Panel header="Supported Packaging Materials" key="packaging_materials">
                  <Row gutter={[32, 16]}>
                    <Col sm={24} md={24} lg={24} xl={12}>
                      <Text strong>Your Packaging</Text>
                    </Col>
                    {PACKAGING_MATERIALS.filter((material) => material.includes('fedex')).map((material) => (
                      <Col sm={24} md={24} lg={24} xl={12} key={material}>
                        <Text strong>{formatPackagingName(material)}</Text>
                      </Col>
                    ))}
                  </Row>
                </Collapse.Panel>
              </Collapse>

              {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}

              {!fedexOfflineLabelsEulaAccepted && (
                <FormButtons
                  onBack={handleCancel}
                  onSubmit={handleSubmit}
                  onSubmitLabel="I Accept"
                  onBackLabel="Cancel"
                  touched={eulaCheckbox}
                  isLoading={changeStateMutation.isPending}
                />
              )}
            </Flex>
          </Skeleton>
          <Divider />
          <Flex direction="horizontal" justifyContent="center">
            <Text type="secondary">
              FedEx service marks are owned by Federal Express Corporation and are used by permission.
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

export default FedexOfflineLabelsAgreement;
